import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  CheckCircle2, XCircle, ShieldCheck, UserPlus, Users, IndianRupee,
  AlertTriangle, RefreshCcw, FileText, Eye, Trash2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

const ADMIN_ROLES = ["super_admin", "founder", "hr_admin"];

// Partner-application status buckets shown as tabs. Ordering matters for UX.
const PARTNER_TABS = [
  { key: "all_active",                 label: "All Applications" },
  { key: "payment_pending",            label: "New / Payment Pending" },
  { key: "payment_verification_pending", label: "Payment Submitted" },
  { key: "under_review",               label: "Under Review" },
  { key: "conditionally_approved",     label: "Conditionally Approved" },
  { key: "active",                     label: "Approved" },
  { key: "rejected",                   label: "Rejected" },
  { key: "draft",                      label: "Drafts" },
];

const OTHER_TABS = [
  { key: "access",    label: "Access Requests",       icon: UserPlus },
  { key: "payments",  label: "Payment Verifications", icon: IndianRupee },
];

const STATUS_BADGE = {
  application_submitted:          { tone: "sky",    label: "Submitted" },
  payment_pending:                { tone: "amber",  label: "Payment Pending" },
  payment_verification_pending:   { tone: "indigo", label: "Payment Submitted" },
  under_review:                   { tone: "blue",   label: "Under Review" },
  conditionally_approved:         { tone: "amber",  label: "Conditionally Approved" },
  active:                         { tone: "emerald",label: "Approved" },
  rejected:                       { tone: "red",    label: "Rejected" },
  suspended:                      { tone: "slate",  label: "Suspended" },
  draft:                          { tone: "slate",  label: "Draft" },
};

export default function AdminApprovals() {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState("partners");   // "partners" | "access" | "payments"
  const [partnerTab, setPartnerTab] = useState("all_active");
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [accessRows, setAccessRows] = useState([]);
  const [paymentRows, setPaymentRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decisionDialog, setDecisionDialog] = useState(null); // { app, action }
  const [viewApp, setViewApp] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [conditions, setConditions] = useState("");

  const loadSummary = useCallback(async () => {
    try {
      const s = await api.get("/admin/approvals/summary");
      setSummary(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load counters");
    }
  }, []);

  const loadPartners = useCallback(async (status) => {
    setLoading(true);
    try {
      const q = await api.get("/admin/approvals/queue", {
        params: { kind: "partner_applications", partner_status: status },
      });
      setRows(q.data.partner_applications || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAccess = useCallback(async () => {
    setLoading(true);
    try {
      const q = await api.get("/admin/approvals/queue", { params: { kind: "access_requests" } });
      setAccessRows(q.data.access_requests || []);
    } finally { setLoading(false); }
  }, []);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const q = await api.get("/admin/approvals/queue", { params: { kind: "payments" } });
      setPaymentRows(q.data.payments || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    if (activeSection === "partners") loadPartners(partnerTab);
    else if (activeSection === "access") loadAccess();
    else if (activeSection === "payments") loadPayments();
  }, [activeSection, partnerTab, loadPartners, loadAccess, loadPayments]);

  if (!ADMIN_ROLES.includes(user?.role)) {
    return (
      <AppLayout>
        <div className="p-6" data-testid="approvals-forbidden">
          <div className="max-w-md mx-auto text-center bg-white border rounded-xl p-8">
            <XCircle className="mx-auto text-red-500 mb-3" size={40}/>
            <h1 className="font-display font-black text-xl">Restricted</h1>
            <p className="text-sm text-[var(--miro-text-muted)] mt-2">
              Only Super Admin / Founder / HR Admin can access the Approval Centre.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const openDecision = (app, action) => {
    setReviewNotes(""); setConditions("");
    setDecisionDialog({ app, action });
  };

  const applyDecision = async () => {
    const { app, action } = decisionDialog;
    try {
      await api.post(`/partner-applications/${app.id}/decision`, {
        action,
        reviewer_notes: reviewNotes || null,
        conditions: conditions || null,
      });
      toast.success(`Application ${action.replace("_", " ")}`);
      setDecisionDialog(null);
      loadSummary();
      loadPartners(partnerTab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    }
  };

  const refresh = () => {
    loadSummary();
    if (activeSection === "partners") loadPartners(partnerTab);
    else if (activeSection === "access") loadAccess();
    else if (activeSection === "payments") loadPayments();
  };

  return (
    <AppLayout>
      <div className="p-6" data-testid="admin-approvals-page">
        <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
          <div>
            <div className="miro-overline">Governance</div>
            <h1 className="font-display font-black text-3xl tracking-tight text-[var(--miro-navy)]">
              Approval Centre
            </h1>
            <p className="text-sm text-[var(--miro-text-muted)] mt-1">
              All submitted partner applications appear here immediately — payment is a separate step.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="approvals-refresh-btn">
            <RefreshCcw size={14} className="mr-1"/> Refresh
          </Button>
        </div>

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
            <SummaryCard label="All submitted" value={summary.partner_applications_pending} accent="navy"    testid="summary-partners-all"/>
            <SummaryCard label="Payment pending" value={summary.partner_payment_pending}    accent="amber"   testid="summary-payment-pending"/>
            <SummaryCard label="Payment submitted" value={summary.partner_verification_pending} accent="indigo" testid="summary-payment-submitted"/>
            <SummaryCard label="Under review" value={summary.partner_under_review}          accent="blue"    testid="summary-under-review"/>
            <SummaryCard label="Access requests" value={summary.access_requests_pending}    accent="emerald" testid="summary-access"/>
            <SummaryCard label="Drafts" value={summary.partner_drafts}                       accent="slate"   testid="summary-drafts"/>
          </div>
        )}

        {/* Section switcher */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <SectionButton active={activeSection === "partners"} onClick={() => setActiveSection("partners")}
            icon={ShieldCheck} label="Partner Applications" testid="section-partners"/>
          {OTHER_TABS.map((t) => (
            <SectionButton key={t.key} active={activeSection === t.key} onClick={() => setActiveSection(t.key)}
              icon={t.icon} label={t.label} testid={`section-${t.key}`}/>
          ))}
        </div>

        {activeSection === "partners" && (
          <>
            <div className="flex gap-2 mb-3 flex-wrap">
              {PARTNER_TABS.map((t) => (
                <button key={t.key}
                  data-testid={`partner-tab-${t.key}`}
                  onClick={() => setPartnerTab(t.key)}
                  className={`text-xs px-3 py-1.5 rounded-full font-semibold border transition
                    ${partnerTab === t.key
                      ? "bg-[var(--miro-navy)] text-white border-[var(--miro-navy)]"
                      : "bg-white text-slate-700 border-[var(--miro-border)] hover:bg-slate-50"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-hidden">
              {loading
                ? <div className="p-6 text-sm text-[var(--miro-text-muted)]">Loading…</div>
                : <PartnerTable rows={rows} onDecision={openDecision} onView={setViewApp} activeStatus={partnerTab}/>}
            </div>
          </>
        )}

        {activeSection === "access" && (
          <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-hidden">
            {loading
              ? <div className="p-6 text-sm text-[var(--miro-text-muted)]">Loading…</div>
              : <AccessRequestList rows={accessRows}/>}
          </div>
        )}

        {activeSection === "payments" && (
          <div className="bg-white border border-[var(--miro-border)] rounded-xl overflow-hidden">
            {loading
              ? <div className="p-6 text-sm text-[var(--miro-text-muted)]">Loading…</div>
              : <PaymentList rows={paymentRows}/>}
          </div>
        )}
      </div>

      {/* Decision dialog */}
      <Dialog open={!!decisionDialog} onOpenChange={(o) => !o && setDecisionDialog(null)}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decisionDialog?.action?.replace("_", " ")} — {decisionDialog?.app?.payment_reference}
            </DialogTitle>
            <DialogDescription>
              {decisionDialog?.action === "delete" ? (
                <span className="text-red-700">
                  This will <b>permanently delete</b> the application, its payment
                  records, agreements and uploaded documents. This cannot be undone.
                </span>
              ) : (decisionDialog?.action === "approve"
                    && (decisionDialog?.app?.status === "payment_pending"
                        || decisionDialog?.app?.status === "application_submitted")) ? (
                <span className="text-amber-800">
                  <b>Waiving the onboarding fee.</b> This partner did not
                  complete payment, but you're activating them anyway. Their
                  partner code + Digital ID will be issued now. Action is
                  audit-logged.
                </span>
              ) : (
                "This action is audit-logged. The partner code " + (decisionDialog?.app?.payment_reference || "") + " will not change."
              )}
            </DialogDescription>
          </DialogHeader>
          {decisionDialog?.action === "conditionally_approve" && (
            <div>
              <Label>Conditions the partner must complete</Label>
              <Textarea rows={2} value={conditions} onChange={(e) => setConditions(e.target.value)}
                data-testid="decision-conditions" placeholder="e.g. Upload signed IC-38 certificate within 15 days"/>
            </div>
          )}
          <div>
            <Label>Reviewer notes (internal)</Label>
            <Textarea rows={3} value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)}
              data-testid="decision-notes"
              placeholder={
                decisionDialog?.action === "reject" ? "Required — reason for rejection"
                : decisionDialog?.action === "delete" ? "Optional — reason recorded in audit log"
                : "Optional"
              }
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog(null)}>Cancel</Button>
            <Button onClick={applyDecision} data-testid="decision-confirm-btn"
              className={decisionDialog?.action === "delete"
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "bg-[var(--miro-navy)] text-white"}>
              {decisionDialog?.action === "delete" ? "Delete permanently" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View application details dialog */}
      <Dialog open={!!viewApp} onOpenChange={(o) => !o && setViewApp(null)}>
        <DialogContent className="max-w-2xl" data-testid="view-app-dialog">
          <DialogHeader>
            <DialogTitle>{viewApp?.payment_reference || "Application"}</DialogTitle>
            <DialogDescription>
              Submitted {fmt(viewApp?.submitted_at || viewApp?.created_at)}
            </DialogDescription>
          </DialogHeader>
          {viewApp && <AppDetailBlock app={viewApp}/>}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function SectionButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`text-sm px-4 py-2 rounded-full font-semibold flex items-center gap-2 transition
        ${active ? "bg-[var(--miro-navy)] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
      <Icon size={14}/> {label}
    </button>
  );
}

function SummaryCard({ label, value, accent, testid }) {
  const accents = {
    navy:    "border-[var(--miro-navy)]/40 bg-white",
    emerald: "border-emerald-200 bg-emerald-50",
    indigo:  "border-indigo-200 bg-indigo-50",
    blue:    "border-sky-200 bg-sky-50",
    amber:   "border-amber-200 bg-amber-50",
    slate:   "border-slate-200 bg-slate-50",
  };
  return (
    <div className={`rounded-xl border p-4 ${accents[accent] || accents.navy}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-[var(--miro-text-muted)]">{label}</div>
      <div className="font-display font-black text-3xl mt-1">{value ?? 0}</div>
    </div>
  );
}

function PartnerTable({ rows, onDecision, onView, activeStatus }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  if (!rows.length) {
    return <Empty label="No applications in this tab." testid="partners-empty"/>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="partners-table">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
          <tr>
            <th className="text-left px-4 py-2">Application #</th>
            <th className="text-left px-4 py-2">Applicant</th>
            <th className="text-left px-4 py-2">Contact</th>
            <th className="text-left px-4 py-2">Track / Category</th>
            <th className="text-left px-4 py-2">Application</th>
            <th className="text-left px-4 py-2">Payment</th>
            <th className="text-left px-4 py-2">Docs</th>
            <th className="text-left px-4 py-2">Submitted</th>
            <th className="text-right px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((r) => {
            const app_status = r.application_status || (r.status === "draft" ? "draft" : "submitted");
            const pay_status = r.payment_status || paymentStatusFromStatus(r.status);
            const docCount = Array.isArray(r.documents) ? r.documents.length : (r.document_count || 0);
            return (
              <tr key={r.id} data-testid={`partner-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-mono font-semibold text-[var(--miro-navy)]">{r.payment_reference || r.application_number || "—"}</div>
                  <div className="text-[10px] text-slate-500">{r.id.slice(0, 8)}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-semibold">
                    {[r.first_name, r.middle_name, r.last_name].filter(Boolean).join(" ") || "—"}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div>{r.email || "—"}</div>
                  <div className="text-slate-500">{r.mobile || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <Badge tone={r.partner_type === "franchise" ? "amber" : "emerald"}>
                    {r.partner_type === "franchise" ? "Franchise" : "Professional"}
                  </Badge>
                  <div className="text-[11px] mt-1 text-slate-600">
                    {(r.primary_category || "").replace(/_/g, " ")}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={r.status === "draft" ? "draft" : (app_status === "submitted" ? "application_submitted" : app_status)}/>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={pay_status}/>
                </td>
                <td className="px-4 py-3 text-xs">
                  <span className="inline-flex items-center gap-1">
                    <FileText size={12}/> {docCount}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {fmt(r.submitted_at || r.created_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-col gap-1 items-end">
                    <Button size="sm" variant="outline" onClick={() => onView(r)}
                            data-testid={`view-btn-${r.id}`}>
                      <Eye size={12} className="mr-1"/> View
                    </Button>
                    {(r.status === "under_review" || r.status === "conditionally_approved") && (
                      <>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7"
                                onClick={() => onDecision(r, "approve")}
                                data-testid={`approve-btn-${r.id}`}>
                          <CheckCircle2 size={12} className="mr-1"/> Approve
                        </Button>
                        {r.status !== "conditionally_approved" && (
                          <Button size="sm" variant="outline" className="h-7"
                                  onClick={() => onDecision(r, "conditionally_approve")}
                                  data-testid={`conditional-btn-${r.id}`}>
                            <AlertTriangle size={12} className="mr-1"/> Conditional
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-7 text-red-600 border-red-200"
                                onClick={() => onDecision(r, "reject")}
                                data-testid={`reject-btn-${r.id}`}>
                          <XCircle size={12} className="mr-1"/> Reject
                        </Button>
                      </>
                    )}
                    {(r.status === "payment_pending" || r.status === "application_submitted") && isSuperAdmin && (
                      <>
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white h-7"
                                onClick={() => onDecision(r, "approve")}
                                data-testid={`waive-approve-btn-${r.id}`}
                                title="Approve without payment (super admin only — waives the onboarding fee)">
                          <CheckCircle2 size={12} className="mr-1"/> Approve without payment
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-red-600 border-red-200"
                                onClick={() => onDecision(r, "reject")}
                                data-testid={`reject-btn-${r.id}`}>
                          <XCircle size={12} className="mr-1"/> Reject
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost"
                            className="h-7 text-red-700 hover:bg-red-50"
                            onClick={() => onDecision(r, "delete")}
                            data-testid={`delete-btn-${r.id}`}
                            title="Permanently delete this application (super_admin only)">
                      <Trash2 size={12}/>
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AppDetailBlock({ app }) {
  const name = [app.first_name, app.middle_name, app.last_name].filter(Boolean).join(" ") || "—";
  const fee = app.fee_snapshot || {};
  return (
    <div className="space-y-3 text-sm">
      <Row label="Applicant" value={name}/>
      <Row label="Application #" value={<span className="font-mono">{app.payment_reference || "—"}</span>}/>
      <Row label="Email" value={app.email || "—"}/>
      <Row label="Mobile" value={app.mobile || "—"}/>
      <Row label="Track" value={app.partner_type === "franchise" ? "Franchise Partner" : "Professional Partner"}/>
      <Row label="Primary category" value={(app.primary_category || "").replace(/_/g, " ")}/>
      <Row label="Application status" value={<StatusBadge status={app.application_status || app.status}/>}/>
      <Row label="Payment status" value={<StatusBadge status={app.payment_status || paymentStatusFromStatus(app.status)}/>}/>
      <Row label="Account status" value={app.account_status || "—"}/>
      <Row label="Submitted at" value={fmt(app.submitted_at || app.created_at)}/>
      <Row label="Applicable fee" value={fee.total != null ? inr(fee.total) : "—"}/>
      {app.conditions && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
          Conditional approval note: {app.conditions}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 w-40 shrink-0">{label}</div>
      <div className="text-sm font-semibold text-slate-800 break-words">{value}</div>
    </div>
  );
}

function AccessRequestList({ rows }) {
  if (!rows.length) return <Empty label="No employee access requests pending." testid="access-empty"/>;
  return (
    <div className="divide-y">
      {rows.map((r) => (
        <div key={r.id} className="p-4 flex items-start justify-between gap-4" data-testid={`access-row-${r.id}`}>
          <div className="min-w-0">
            <div className="font-semibold">{r.name}</div>
            <div className="text-xs text-[var(--miro-text-muted)] truncate">
              {r.email} · {r.role_requested || "role TBD"} · {r.department || "—"}
            </div>
            {r.reason && <div className="text-sm mt-1 line-clamp-2">{r.reason}</div>}
          </div>
          <Button asChild size="sm" variant="outline" data-testid={`access-open-btn-${r.id}`}>
            <Link to="/admin/access-requests">Open in Access Requests</Link>
          </Button>
        </div>
      ))}
    </div>
  );
}

function PaymentList({ rows }) {
  if (!rows.length) return <Empty label="No payments waiting for verification." testid="payments-empty"/>;
  return (
    <div className="divide-y">
      {rows.map((r) => (
        <div key={r.id} className="p-4 flex items-start justify-between gap-4" data-testid={`payment-row-${r.id}`}>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-semibold text-[var(--miro-navy)]">{r.payment_reference}</span>
              <Badge>UTR {r.utr}</Badge>
            </div>
            <div className="text-xs mt-1">
              Submitted <b>{inr(r.amount_submitted)}</b> of expected {inr(r.amount_expected)} · method {r.payment_method}
            </div>
          </div>
          <Button asChild size="sm" variant="outline" data-testid={`payment-open-btn-${r.id}`}>
            <Link to="/admin/partner-payments">Open in Payment Verification</Link>
          </Button>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const b = STATUS_BADGE[status] || { tone: "slate", label: status };
  const tones = {
    slate:   "bg-slate-100 text-slate-700",
    amber:   "bg-amber-100 text-amber-800",
    sky:     "bg-sky-100 text-sky-800",
    indigo:  "bg-indigo-100 text-indigo-800",
    blue:    "bg-blue-100 text-blue-800",
    emerald: "bg-emerald-100 text-emerald-800",
    red:     "bg-red-100 text-red-800",
  };
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${tones[b.tone] || tones.slate}`}>{b.label}</span>;
}

function Badge({ children, tone = "slate" }) {
  const tones = {
    slate:   "bg-slate-100 text-slate-700",
    amber:   "bg-amber-100 text-amber-800",
    emerald: "bg-emerald-100 text-emerald-800",
  };
  return <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${tones[tone]}`}>{children}</span>;
}

function Empty({ label, testid }) {
  return <div className="p-8 text-center text-sm text-[var(--miro-text-muted)]" data-testid={testid}>{label}</div>;
}

/** Derive a payment_status label from the coarse status field for older rows
 * that don't yet have a denormalised payment_status. */
function paymentStatusFromStatus(status) {
  switch (status) {
    case "application_submitted":
    case "payment_pending":                return "payment_pending";
    case "payment_verification_pending":   return "payment_verification_pending";
    case "under_review":
    case "conditionally_approved":
    case "active":                         return "payment_verification_pending"; // verified
    case "rejected":                       return "rejected";
    case "draft":                          return "draft";
    default:                               return status;
  }
}
